/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { Box, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { format, fromObjectEntries, monthNames, objectEntries } from '@poweralpha/common';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { core } from 'zod';
import InputKeyValue from '../display';
import { IOSSwitch } from '../IOSSwitch';
import { BaseTextField } from './BaseTextField';
import { getErrorMessage } from './form';
import { useFieldContext } from './formContext';

interface Props {
  label?: string;
  rightUnit?: string;
  size?: 'small' | 'xs';
  displayOnly?: boolean;
  disabled?: boolean;
}

const monthMap = fromObjectEntries(monthNames.map((month, i) => [i, month]));

function FormMonthlyDataFields({ label, rightUnit, size, displayOnly, disabled }: Readonly<Props>) {
  const field = useFieldContext<Array<string>>();
  const fieldValue = useMemo(
    () => field.state.value as unknown as Array<string>,
    [field.state.value],
  );

  const [oneValForYear, setOneValForYear] = useState(fieldValue.length === 1);

  const errors = useMemo(() => {
    const errs = {
      ...field.form.state.errorMap.onMount,
      ...field.form.state.errorMap.onSubmit,
      ...field.form.state.errorMap.onChange,
    } as Record<string, Array<core.$ZodIssue>>;
    const filteredErrorEntries = objectEntries(errs).filter(([key]) => key.includes(field.name));
    if (filteredErrorEntries[0]) {
      const [, errors] = filteredErrorEntries[0];
      const [error] = errors;
      return {
        [(error.path?.at(-1) ?? 0).toString()]: getErrorMessage(
          label?.replace(/\s*(?:\[[^\]]*\]|\([^)]*\))+$/g, '') ?? field.name,
          error,
        ),
      };
    }

    return {};
  }, [field.form.state.errorMap, field.name, label]);

  const handleSimpleToggleChange = useCallback(() => {
    if (oneValForYear) {
      field.handleChange(new Array(12).fill(fieldValue[0]));
    }
    if (!oneValForYear) {
      field.handleChange(fieldValue ? [fieldValue[0]] : ['']);
    }
    field.validate('change');
    setOneValForYear((prevState) => !prevState);
  }, [fieldValue, oneValForYear, field.validate, field]);

  const handleChange = useCallback(
    (value: string, index = 0) => {
      const newVals = [...fieldValue];
      newVals[index] = value;
      field.handleChange(newVals);
    },
    [fieldValue, field],
  );

  const fields = useMemo(() => {
    if (displayOnly) {
      return fieldValue.length === 1 ? (
        <Grid size={12}>
          <InputKeyValue label="" value={fieldValue[0]} />
        </Grid>
      ) : (
        <Grid container spacing={1}>
          {fieldValue.map((value, i) => (
            <Grid key={`month-field-${field.name}-${monthMap[i]}`} size={{ lg: 2, md: 3 }}>
              <Stack>
                <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                  {monthMap[i]}
                </Typography>
                <Box>{format(Number(value))}</Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      );
    }

    return fieldValue.length === 1 ? (
      <Grid size={12}>
        <BaseTextField
          value={fieldValue[0]}
          name={field.name}
          label=""
          onChange={(e) => handleChange(e.target.value)}
          error={Boolean(errors['0'])}
          helperText={errors['0']}
          rightUnit={rightUnit}
          size={size}
          disabled={disabled}
        />
      </Grid>
    ) : (
      <Grid container spacing={1}>
        {fieldValue.map((value, i) => (
          <Grid key={`month-field-${field.name}-${monthMap[i]}`} size={{ lg: 2, md: 3 }}>
            <BaseTextField
              fullWidth
              variant="outlined"
              name={field.name}
              label={monthMap[i]}
              value={value}
              error={Boolean(errors[i.toString()])}
              onChange={(e) => handleChange(e.target.value, i)}
              size={size}
              disabled={disabled}
            />
          </Grid>
        ))}
      </Grid>
    );
  }, [displayOnly, fieldValue, field.name, errors, handleChange, size, disabled, rightUnit]);

  useEffect(() => {
    setOneValForYear(fieldValue.length === 1);
  }, [fieldValue.length]);

  return useMemo(
    () => (
      <Grid container spacing={0.3}>
        <Grid size={12}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: '100%',
            }}
          >
            <Grid size="grow">
              <Typography>
                {label ?? (oneValForYear ? 'Annual Value' : 'Monthly Values')}
              </Typography>
            </Grid>
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={!oneValForYear}
                  onChange={handleSimpleToggleChange}
                  data-testid={`${field.name}-monthly-switch`}
                />
              }
              label="Monthly"
              disabled={disabled || displayOnly}
              sx={{ pb: 1 }}
            />
          </Stack>
        </Grid>
        {fields}
        {!oneValForYear && !isEmpty(errors) && (
          <>
            <Typography
              sx={{
                color: 'error.main',
                fontSize: 12,
                paddingLeft: 1,
              }}
            >
              {Object.values(errors)[0]}
            </Typography>
            <Box
              component="div"
              sx={{
                p: 1,
              }}
            />
          </>
        )}
      </Grid>
    ),
    [
      label,
      oneValForYear,
      handleSimpleToggleChange,
      field.name,
      disabled,
      errors,
      fields,
      displayOnly,
    ],
  );
}

export default FormMonthlyDataFields;
